import { useEffect, useRef, forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

import { ApplyWrapper } from '@lib/ui';
import { mergeRefs } from '@lib/hooks';
import { cn, cva, type VariantProps, setThisClass } from '@lib/utils';
import type { ColorType } from '@lib/types';

const checkboxVariants = cva(
  [
    // Layout
    'form-checkbox relative inline-block shrink-0 cursor-pointer appearance-none overflow-hidden border',
    // Focus
    'outline-hidden focus:outline-hidden',
    // Transitions
    'transition-all duration-200 ease-out',
    // Print
    '[print-color-adjust:exact]',
    // Before pseudo-element (checkmark container)
    'before:absolute before:inset-0 before:h-full before:w-full before:origin-bottom before:scale-0',
    'before:duration-200 before:ease-in-out',
    // Checked state
    'checked:before:scale-100',
    // Indeterminate state
    'indeterminate:before:scale-100 indeterminate:before:transition-none',
    // Hover
    'hover:shadow-lg',
  ],
  {
    variants: {
      variant: {
        basic: [
          // Border
          'border-line',
          // Background origin for gradient borders
          'bg-origin-border',
          // Checkmark icon styling
          'before:bg-center before:bg-no-repeat before:[background-size:100%_100%] before:[background-image:var(--tw-thumb)]',
          // Checked state (using legacy --color-this which works with modifiers)
          'checked:border-this checked:bg-this',
          // Indeterminate state
          'indeterminate:border-this indeterminate:bg-this',
          // Hover/focus states
          'hover:border-this focus:border-this',
        ],
        outlined: [
          // Border
          'border-line',
          // Checkmark icon styling (mask-based for outlined variant)
          'before:bg-this before:[mask-image:var(--tw-thumb)]',
          // Checked state
          'checked:border-this',
          // Hover/focus states
          'hover:border-this focus:border-this',
        ],
      },
      size: {
        xs: 'size-3.5',
        sm: 'size-4',
        md: 'size-4.5',
        lg: 'size-6',
        xl: 'size-7',
      },
      rounded: {
        default: 'rounded-sm',
        full: 'rounded-full',
      },
      disabled: {
        true: [
          // Disabled styling
          'pointer-events-none select-none opacity-60',
          'border-line bg-surface-raised',
          'before:bg-fg-muted before:[mask-image:var(--tw-thumb)]',
        ],
        false: '',
      },
    },
    defaultVariants: {
      variant: 'basic',
      size: 'md',
      rounded: 'default',
      disabled: false,
    },
  }
);

const labelVariants = cva(['inline-flex items-center gap-2 cursor-pointer'], {
  variants: {
    disabled: {
      true: 'cursor-not-allowed opacity-60',
      false: '',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

type CheckboxProps = {
  color?: Exclude<ColorType, 'neutral'>;
  label?: ReactNode;
  indeterminate?: boolean;
  classNames?: {
    input?: string;
    label?: string;
    labelText?: string;
  };
  labelProps?: Record<string, unknown>;
} & VariantProps<typeof checkboxVariants> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'color' | 'size'>;

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      variant = 'basic',
      size = 'md',
      rounded = 'default',
      color = 'primary',
      type = 'checkbox',
      className,
      classNames = {},
      label,
      disabled = false,
      indeterminate,
      labelProps,
      ...rest
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = Boolean(indeterminate);
      }
    }, [indeterminate]);

    return (
      <ApplyWrapper
        when={!!label}
        wrapper={(children) => (
          <label className={cn(labelVariants({ disabled }), classNames?.label)} {...labelProps}>
            {children}
            <span className={cn('select-none', classNames?.labelText)}>{label}</span>
          </label>
        )}
      >
        <input
          className={cn(
            checkboxVariants({ variant, size, rounded, disabled }),
            setThisClass(color),
            className,
            classNames?.input
          )}
          disabled={disabled}
          data-disabled={disabled || undefined}
          data-indeterminate={indeterminate || undefined}
          ref={mergeRefs(inputRef, ref)}
          type={type}
          {...rest}
        />
      </ApplyWrapper>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
export type { CheckboxProps };
